import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    stepper: {
      insertStepper: (stepCount?: number) => ReturnType;
    };
  }
}

export interface StepperOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

function genId(): string {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof (crypto as any).randomUUID === 'function'
    ) {
      return (crypto as any).randomUUID().replace(/-/g, '').slice(0, 8);
    }
  } catch {
    // noop
  }
  return Math.random().toString(36).slice(2, 10);
}

export const Stepper = Node.create<StepperOptions>({
  name: 'stepper',
  group: 'block',
  content: 'step+',
  defining: true,
  isolating: true,

  addOptions() {
    return { HTMLAttributes: {}, view: null };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="stepper"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name, class: 'stepper' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addNodeView() {
    if (!this.options.view) return null;
    return ReactNodeViewRenderer(this.options.view);
  },

  addCommands() {
    return {
      insertStepper:
        (stepCount = 2) =>
        ({ commands }) => {
          const n = Math.max(1, Math.min(9, stepCount));
          const steps = Array.from({ length: n }, () => ({
            type: 'step',
            attrs: { id: genId() },
            content: [
              { type: 'stepTitle' },
              { type: 'stepContent', content: [{ type: 'paragraph' }] },
            ],
          }));
          return commands.insertContent({
            type: this.name,
            content: steps,
          });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-s': () => this.editor.commands.insertStepper(),
    };
  },
});

export interface StepOptions {
  HTMLAttributes: Record<string, any>;
}

export const Step = Node.create<StepOptions>({
  name: 'step',
  content: 'stepTitle stepContent',
  defining: true,
  isolating: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute('id'),
        renderHTML: (attrs) => (attrs.id ? { id: attrs.id } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="step"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name, class: 'step' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },
});

export interface StepTitleOptions {
  HTMLAttributes: Record<string, any>;
}

export const StepTitle = Node.create<StepTitleOptions>({
  name: 'stepTitle',
  content: 'inline*',
  defining: true,
  isolating: true,
  marks: 'bold italic code link',

  addOptions() {
    return { HTMLAttributes: {} };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="stepTitle"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name, class: 'step-title' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { state, view } = this.editor;
        const { $from } = state.selection;
        if ($from.parent.type.name !== this.name) return false;
        // Find the enclosing step and jump into its stepContent's first child.
        let stepDepth = -1;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === 'step') {
            stepDepth = d;
            break;
          }
        }
        if (stepDepth < 0) return false;
        const stepPos = $from.before(stepDepth);
        const step = state.doc.nodeAt(stepPos);
        if (!step) return false;
        let contentPos: number | null = null;
        step.forEach((child, offset) => {
          if (contentPos == null && child.type.name === 'stepContent') {
            // +1 for step opening, +1 for stepContent opening, +1 for first child opening
            contentPos = stepPos + 1 + offset + 2;
          }
        });
        if (contentPos == null) return false;
        const tr = state.tr.setSelection(
          TextSelection.near(state.doc.resolve(contentPos)),
        );
        view.dispatch(tr);
        return true;
      },
      Backspace: () => {
        const { state, view } = this.editor;
        const { $from, empty } = state.selection;
        if (!empty) return false;
        if ($from.parent.type.name !== this.name) return false;
        if ($from.parentOffset !== 0) return false;
        if ($from.parent.content.size > 0) return false;
        // Pointing at the start of an empty stepTitle — delete this step
        // (or the whole stepper if it was the only one).
        let stepDepth = -1;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === 'step') {
            stepDepth = d;
            break;
          }
        }
        if (stepDepth < 0) return false;
        const stepPos = $from.before(stepDepth);
        const step = state.doc.nodeAt(stepPos);
        if (!step) return false;
        const stepperPos = $from.before(stepDepth - 1);
        const stepper = state.doc.nodeAt(stepperPos);
        if (!stepper || stepper.type.name !== 'stepper') return false;
        const tr = state.tr;
        if (stepper.childCount <= 1) {
          tr.delete(stepperPos, stepperPos + stepper.nodeSize);
        } else {
          tr.delete(stepPos, stepPos + step.nodeSize);
          tr.setSelection(
            TextSelection.near(tr.doc.resolve(Math.max(stepPos - 1, 0)), -1),
          );
        }
        view.dispatch(tr);
        return true;
      },
    };
  },
});

export interface StepContentOptions {
  HTMLAttributes: Record<string, any>;
}

export const StepContent = Node.create<StepContentOptions>({
  name: 'stepContent',
  content: 'block+',
  defining: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="stepContent"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name, class: 'step-content' },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
      0,
    ];
  },
});
